import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  deleteTenant,
  listTenantDeletions,
  type TenantDeletion,
  type TenantDeletionSummary,
} from "./tenantsApi";
import {
  consoleCanRetry,
  deletionBannerKey,
  deletionErrorKey,
  deletionSettled,
  outstandingTotal,
  slugConfirmed,
  storeKey,
  waitingOnProvisioner,
} from "./tenantDeletion";
import { ApiError, SessionExpiredError } from "@web/lib/api";
import { formatDate } from "@web/lib/date";
import { Card, Modal } from "@web/ui";

// THE DEMO-TENANT DELETIONS THIS CONSOLE CAN STILL SEE — on the DIRECTORY and not on the page of a tenant
// that no longer exists (ADR-0023 §6.3a, task dc216a05).
//
// WHY IT IS HERE AT ALL, which is the part worth reading. ADR-0023 deliberately adds NO background worker
// that retries the external cleanup, and that choice is only defensible because an outstanding obligation is
// visible without being looked for. A deletion commits and the tenant's page stops existing — so the one
// surface that can still carry what the deletion owes is the list the operator was about to return to.
//
// IT CARRIES BOTH HALVES OF THE ANSWER, and the second half is the correction of §6.3a over the earlier
// draft. The normal successful deletion answers `incomplete` with exactly one `keycloak_federation` row,
// because the realm IdP alias is the provisioning component's to remove (§6.2a) — so until that component's
// next pass (default 30s) every healthy deletion is an unsettled one. If this list showed only the unsettled
// ones, the operator would watch a row they could do nothing about silently VANISH when it settled, which is
// indistinguishable from a cleanup that was abandoned. So the recently completed ones are listed too, and a
// deletion the component finished while nobody was watching turns up as "Valmis" on the next read: `done` is
// computed from the ledger count at read time, so no separate completion signal is needed.
//
// THE REPORT NAMES THE STORE, NEVER JUST A COUNT (§6.2a). "1 jäljellä" tells an operator to press something,
// and for the federation row pressing is exactly wrong — this console has no call path to the realm at all
// (ADR-0014 §7.2). So a row whose only outstanding store is that one gets the WAITING sentence and NO retry
// button; see `consoleCanRetry`, which is where that reading lives.
//
// THE RETRY ASKS FOR THE SLUG AGAIN, because §7 item 3 asks for it on every press and makes no exception for
// retries. There is no `tenants` row left to compare it against: the server matches what was typed against
// the ledger's `tenant_slug`, or the `TENANT_DELETED` audit row's slug once the ledger is empty. It is a
// legitimate act in two states — with obligations left it works them, and with none left it answers `done`
// and changes nothing — which is why a 404 means something narrow (this console has no record of the id,
// i.e. somebody else finished it) and is not rendered as a failed cleanup.
//
// NOTHING IS DRAWN WHEN THE LIST IS EMPTY, and a failed read is said quietly. On a platform where nobody has
// deleted a demo tenant this is not a section at all; and a read that dropped must not put an error banner
// over a directory whose own list loaded, so it says one line and leaves the page alone.

/** The retry's confirmation, over one named deletion. Absent means no dialog is open. */
type Retry = {
  deletion: TenantDeletionSummary;
  confirm: string;
  submitting: boolean;
  error: string | null;
};

export function TenantDeletionsSection() {
  const { t } = useTranslation();
  const [deletions, setDeletions] = useState<TenantDeletionSummary[] | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [retry, setRetry] = useState<Retry | null>(null);
  /**
   * What the last retry answered, so its outcome is said rather than only reflected in the refreshed list.
   *
   * With its own tone: a retry that still leaves a store outstanding must not wear the green of one that
   * finished, which is the reading that would make an operator stop watching a cleanup that is not done.
   */
  const [banner, setBanner] = useState<{ text: string; settled: boolean } | null>(null);
  const confirmRef = useRef<HTMLInputElement>(null);

  const reload = useCallback((signal?: AbortSignal) => {
    listTenantDeletions(signal)
      .then((rows) => {
        setDeletions(rows);
        setLoadFailed(false);
      })
      .catch((err: unknown) => {
        if (signal?.aborted || err instanceof SessionExpiredError) return;
        // The LIST keeps whatever it had: a dropped refresh must not throw away an outstanding obligation
        // the operator can still see and act on, in order to report the newer failure.
        setLoadFailed(true);
      });
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    reload(controller.signal);
    return () => controller.abort();
  }, [reload]);

  const onRetry = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      if (!retry || retry.submitting) return;

      const { deletion, confirm } = retry;
      setRetry((r) => r && { ...r, submitting: true, error: null });
      deleteTenant(deletion.tenantId, { slug: confirm })
        .then((result: TenantDeletion) => {
          setRetry(null);
          setBanner({
            text: t(deletionBannerKey(result.status), { tenant: result.slug || deletion.slug }),
            settled: deletionSettled(result.status),
          });
          // Re-read rather than patching the row from the answer: `done` is the ledger's count at read
          // time, and the provisioning component may have settled its own row meanwhile. One source.
          reload();
        })
        .catch((err: unknown) => {
          if (err instanceof SessionExpiredError) return;
          const key =
            err instanceof ApiError
              ? deletionErrorKey(err.status, err.problem)
              : "tenants.delete.error";
          setRetry((r) => r && { ...r, submitting: false, error: t(key) });
          // A 404 means this id is no longer on record — somebody else finished it — so the row the
          // operator is looking at is stale. Refresh it rather than leaving a button over nothing.
          if (err instanceof ApiError && err.status === 404) reload();
        });
    },
    [retry, reload, t],
  );

  if (deletions === null || deletions.length === 0) {
    return loadFailed ? (
      <p className="tenants__slug-status" role="status" data-testid="tenant-deletions-load-error">
        {t("tenants.delete.listError")}
      </p>
    ) : null;
  }

  const outstanding = deletions.filter((row) => !deletionSettled(row.status));
  const settled = deletions.filter((row) => deletionSettled(row.status));
  const titleId = "tenant-deletions-title";

  return (
    // A labelled region, like the tenant page's own sections: this is a second list on a page whose
    // <h1> is the directory, so a screen reader reaching it has to be told what it is a list OF.
    <Card as="section" aria-labelledby={titleId} data-testid="tenant-deletions">
      <h2 id={titleId}>{t("tenants.delete.listTitle")}</h2>
      <p className="tenant-section__note" role="note">
        {t("tenants.delete.listNote")}
      </p>

      {banner && (
        <p
          className={`banner banner--${banner.settled ? "success" : "error"}`}
          role="status"
          data-testid="tenant-deletions-banner"
        >
          {banner.text}
        </p>
      )}
      {loadFailed && (
        <p className="tenants__slug-status" role="status" data-testid="tenant-deletions-load-error">
          {t("tenants.delete.listError")}
        </p>
      )}

      <ul className="tenant-deletions__list">
        {outstanding.map((row) => (
          <li
            key={row.tenantId}
            className="banner banner--error"
            data-testid="tenant-deletion-outstanding"
          >
            <p>
              <code className="tenants__slug">{row.slug}</code>{" "}
              {t("tenants.delete.listIncomplete", {
                date: formatDate(new Date(row.deletedAt)),
                rows: outstandingTotal(row.outstanding),
              })}
            </p>

            {/* The STORE, by name, with its count and the store's own last refusal — never a bare total.
                See the header comment: a total invites a press, and for the federation row a press is the
                wrong act. */}
            <ul>
              {row.outstanding.map((store) => (
                <li key={store.store} data-testid="tenant-deletion-store">
                  {t(storeKey(store.store))}: {Number(store.count)}
                  {store.lastError && (
                    <>
                      {" — "}
                      <span className="tenant-deletions__error">{store.lastError}</span>
                    </>
                  )}
                </li>
              ))}
            </ul>

            {waitingOnProvisioner(row.status, row.outstanding) ? (
              <p data-testid="tenant-deletion-waiting">{t("tenants.delete.waitingOnProvisioner")}</p>
            ) : (
              consoleCanRetry(row.outstanding) && (
                <button
                  type="button"
                  className="btn"
                  onClick={() =>
                    setRetry({ deletion: row, confirm: "", submitting: false, error: null })
                  }
                  data-testid="tenant-deletion-retry"
                >
                  {t("tenants.delete.retry")}
                </button>
              )
            )}
          </li>
        ))}

        {settled.map((row) => (
          <li key={row.tenantId} data-testid="tenant-deletion-done">
            <code className="tenants__slug">{row.slug}</code>{" "}
            {t("tenants.delete.listDone", { date: formatDate(new Date(row.deletedAt)) })}
          </li>
        ))}
      </ul>

      {retry && (
        <Modal
          role="alertdialog"
          className="freeze__dialog"
          labelledBy="tenant-deletion-retry-title"
          testId="tenant-deletion-retry-dialog"
          onClose={() => setRetry(null)}
          closeDisabled={retry.submitting}
          initialFocus={confirmRef}
        >
          <h2 id="tenant-deletion-retry-title">
            {t("tenants.delete.retryTitle", { tenant: retry.deletion.slug })}
          </h2>
          <p>{t("tenants.delete.retryBody")}</p>

          <form className="form" onSubmit={onRetry} noValidate>
            {/* The slug again, on this press as on the first (§7 item 3). By now there is no tenant row to
                compare it against — the server matches it against the ledger or the audit row. */}
            <label htmlFor="tenant-deletion-retry-confirm">
              {t("tenants.delete.confirmLabel", { tenant: retry.deletion.slug })}
            </label>
            <input
              id="tenant-deletion-retry-confirm"
              ref={confirmRef}
              type="text"
              value={retry.confirm}
              onChange={(e) => setRetry((r) => r && { ...r, confirm: e.target.value })}
              autoComplete="off"
              autoCapitalize="off"
              spellCheck={false}
              disabled={retry.submitting}
              data-testid="tenant-deletion-retry-confirm"
            />

            {retry.error && (
              <p className="field-error" role="alert" data-testid="tenant-deletion-retry-error">
                {retry.error}
              </p>
            )}

            <div className="modal__actions">
              <button
                type="button"
                className="btn"
                onClick={() => setRetry(null)}
                disabled={retry.submitting}
              >
                {t("tenants.cancel")}
              </button>
              <button
                type="submit"
                className="btn btn--danger"
                disabled={
                  !slugConfirmed(retry.confirm, retry.deletion.slug) || retry.submitting
                }
                data-testid="tenant-deletion-retry-submit"
              >
                {retry.submitting ? t("tenants.delete.retrying") : t("tenants.delete.retry")}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </Card>
  );
}
